/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useNeedleTheme } from '@neo4j-ndl/react';
import classNames from 'classnames';

import type { CommonProps } from '../../_common/types';
import {
  Neo4JAiColorDark as Neo4JAiColorDarkIcon,
  Neo4JAiColorLight as Neo4JAiColorLightIcon,
} from '../../icons/manual/Neo4JAiColor';

type PresenceProps = {
  /** Whether the AI is thinking. Animates the underlying SVG */
  isThinking?: boolean;
};

const Presence = ({
  className,
  isThinking = false,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'svg', PresenceProps>) => {
  const classes = classNames('ndl-ai-presence', className, {
    'ndl-thinking': isThinking === true,
  });
  const { theme } = useNeedleTheme();

  const Component =
    theme === 'light' ? Neo4JAiColorLightIcon : Neo4JAiColorDarkIcon;

  return (
    <Component
      ref={ref}
      className={classes}
      style={style}
      htmlAttributes={htmlAttributes}
      {...(isThinking === true && {
        'aria-hidden': 'false',
        'aria-label': 'Thinking',
        'aria-live': 'polite',
        role: 'status',
      })}
      {...restProps}
    />
  );
};

Presence.displayName = 'Presence';

export { Presence };
